import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { Button, CircularProgress } from '@mui/material'
import { useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { postDataWithParams } from 'src/consumer/services/profileService'

const ACCEPTED = 'application/pdf,image/*'
// Keep these in sync — the helper copy surfaces DOCUMENT_UPLOAD_MAX_MB to the user.
export const DOCUMENT_UPLOAD_MAX_MB = 20
const MAX_BYTES = DOCUMENT_UPLOAD_MAX_MB * 1024 * 1024
const ALLOWED_EXT = /\.(pdf|png|jpe?g|webp|heic|heif|gif|bmp|tiff?)$/i

/**
 * Dedicated "Upload PDF / Document" affordance for report & prescription attachments.
 *
 * Unlike the image uploader (FilePond/Doka, image-only) this accepts a PDF (or image) and posts it
 * straight to the same report/prescription file endpoint, so the returned fileId lands in the very
 * same attachment list as the images and is persisted by the existing save path. PDFs render through
 * {@link PdfAwareImage}, which falls back to an "Open document" tile when the id isn't an image.
 */
export default function DocumentUploadButton({
  uploadUrl,
  uploadParams = {},
  onAddFileId,
  label = 'Upload PDF / Document',
  disabled = false,
  fullWidth = false,
}) {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  const handleFiles = async (e) => {
    const file = e.target.files?.[0]
    // Clear the input up-front so picking the same file again still fires onChange.
    e.target.value = ''
    if (!file) return

    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
    const isImage = (file.type || '').startsWith('image/')
    // Validate by both MIME type and extension — some browsers report an empty/odd type for PDFs,
    // and we don't want to upload an executable/archive that merely got renamed.
    if (!isPdf && !isImage && !ALLOWED_EXT.test(file.name)) {
      toast.error('Only PDF or image files can be uploaded')
      return
    }
    if (!file.size) {
      toast.error('That file appears to be empty')
      return
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File is too large (max ${DOCUMENT_UPLOAD_MAX_MB} MB)`)
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const resp = await postDataWithParams(uploadUrl, formData, uploadParams)
      if (resp?.status === 200 && resp?.data?.fileId) {
        onAddFileId(resp.data.fileId)
        toast.success(isPdf ? 'PDF uploaded' : 'Document uploaded')
      } else {
        toast.error('Upload failed. Please try again.')
      }
    } catch (err) {
      toast.error('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <>
      <input ref={inputRef} type="file" accept={ACCEPTED} hidden onChange={handleFiles} />
      <Button
        variant="outlined"
        size="small"
        disableElevation
        fullWidth={fullWidth}
        disabled={disabled || uploading}
        startIcon={
          uploading ? (
            <CircularProgress size={16} sx={{ color: '#d97706' }} />
          ) : (
            <UploadFileRoundedIcon sx={{ fontSize: 18 }} />
          )
        }
        onClick={() => inputRef.current?.click()}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '13px',
          color: '#b45309',
          borderColor: 'rgba(217, 119, 6, 0.4)',
          backgroundColor: 'rgba(245, 158, 11, 0.06)',
          borderRadius: '10px',
          px: 1.5,
          '&:hover': {
            borderColor: '#d97706',
            backgroundColor: 'rgba(245, 158, 11, 0.12)',
          },
        }}
      >
        {uploading ? 'Uploading…' : label}
      </Button>
    </>
  )
}
